import {
  CSSProperties,
  createContext,
  PropsWithChildren,
  ReactNode,
} from 'react';
import styled from 'styled-components';
import { colors, s16, s24, s4 } from '@ardoq/design-tokens';
import { header4, text1 } from '@ardoq/typography';
import { TextOverflow, WithPopover } from '@ardoq/popovers';
import { ICON_SELECTOR, IconSize, InfoIcon } from '@ardoq/icons';
import { Box } from '@ardoq/layout';

const IsVerticalCtx = createContext(false);

const KeyLabel = styled.div`
  ${header4};
  display: flex;
  height: 24px;
  align-items: center;
  gap: ${s4};

  & > ${ICON_SELECTOR} {
    color: ${colors.icon};
  }
`;

const VerticalContentValueWrapper = styled.div`
  ${text1};
  display: flex;
  line-height: ${s24};
  padding: ${s4} 0;
`;

type TableListItemProps = {
  label?: ReactNode;
  infoText?: string;
};

const HorizontalTableListItem = ({
  label,
  infoText,
  children,
}: PropsWithChildren<TableListItemProps>) => (
  <tr>
    <td>
      <KeyLabel>
        <TextOverflow>{label}</TextOverflow>
        {infoText && (
          <WithPopover content={infoText}>
            <InfoIcon iconSize={IconSize.SMALL} />
          </WithPopover>
        )}
      </KeyLabel>
    </td>
    <td>{children}</td>
  </tr>
);

const VerticalTableListItem = ({
  label,
  infoText,
  children,
}: PropsWithChildren<TableListItemProps>) => (
  <Box paddingBottom="medium">
    <KeyLabel>
      <TextOverflow>{label}</TextOverflow>
      {infoText && (
        <WithPopover content={infoText}>
          <InfoIcon iconSize={IconSize.SMALL} />
        </WithPopover>
      )}
    </KeyLabel>
    <VerticalContentValueWrapper>{children}</VerticalContentValueWrapper>
  </Box>
);

export const TableListItem = (props: PropsWithChildren<TableListItemProps>) => (
  <IsVerticalCtx.Consumer>
    {isVertical =>
      isVertical ? (
        <VerticalTableListItem {...props} />
      ) : (
        <HorizontalTableListItem {...props} />
      )
    }
  </IsVerticalCtx.Consumer>
);

const TableListHorizontalWrapper = styled.table`
  border-spacing: 0;

  td {
    &:first-child {
      padding: 0 ${s16} ${s16} 0;
      min-width: 120px;
      max-width: 240px;
    }

    &:last-child {
      padding: 0 0 ${s16} ${s16};
      ${text1};
      vertical-align: middle;
      min-width: 120px;
      max-width: 480px;
      & > div > div {
        display: flex;
      }
    }
  }

  & > tr {
    vertical-align: top;
    &:last-child {
      td {
        padding-bottom: 0;
      }
    }
  }
`;

const TableListVerticalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 480px;
`;

type TableListProps = {
  isVertical?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
};
export const TableList = ({
  children,
  isVertical = false,
  style,
}: TableListProps) => {
  const TableListWrapper = isVertical
    ? TableListVerticalWrapper
    : TableListHorizontalWrapper;
  return (
    <IsVerticalCtx.Provider value={isVertical}>
      <TableListWrapper style={style}>{children}</TableListWrapper>
    </IsVerticalCtx.Provider>
  );
};
